import { motion } from "framer-motion";
import { Leaf, Mail, MapPin, Phone, Linkedin, Twitter, Youtube } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-tech-hyd/artifacts/aiwksqrp_tech4earth_logo-removebg-preview.png";

const footerLinks = {
  quickLinks: [
    { name: "About Us", href: "#about" },
    { name: "Our Vision", href: "#vision" },
    { name: "Technology Domains", href: "#technology" },
    { name: "Member Benefits", href: "#opportunities" },
    { name: "Initiatives", href: "#initiatives" },
    { name: "Contact", href: "#contact" },
  ],
  focusAreas: [
    "E-Mobility",
    "Hydrogen Technology",
    "Solar Energy",
    "Autonomous Vehicles",
    "Software Defined Vehicles",
    "Urban Air Mobility & eVTOL",
    "Drone Technology",
    "Smart Infrastructure & V2X",
    "Circular Economy in Battery",
    "Connected Vehicles",
    "Smart City Traffic Optimisation",
    "Bio Fuels",
  ],
};

const socialLinks = [
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Youtube, href: "#", label: "YouTube" },
];

const Footer = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-stone-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Tech4Earth Mobility"
              className="h-16 w-auto object-contain mb-6 rounded-lg"
            />
            <p className="text-stone-400 text-sm leading-relaxed mb-6">
              A mobility-focused non-profit organisation dedicated to the
              development of sustainable Mobility technologies worldwide.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="p-2.5 bg-stone-800 hover:bg-emerald-700 rounded-lg transition-colors"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-stone-400 hover:text-emerald-400 text-sm transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Focus Areas */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">
              Focus Areas
            </h4>
            <ul className="space-y-3">
              {footerLinks.focusAreas.map((area) => (
                <li key={area} className="flex items-center gap-2">
                  <Leaf className="w-3 h-3 text-emerald-500" />
                  <span className="text-stone-400 text-sm">{area}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-stone-400 text-sm">
                  7-1-235/42P42 Suresh Arcade,<br />
                  Prashant Nagar Colony, BK Guda,<br />
                  Begumpet, Secunderabad,<br />
                  Hyderabad - 500016, Telangana
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <a
                  href="mailto:info@tech4earth.net"
                  className="text-stone-400 hover:text-emerald-400 text-sm transition-colors"
                >
                  info@tech4earth.net
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <a
                  href="tel:+919900089415"
                  className="text-stone-400 hover:text-emerald-400 text-sm transition-colors"
                >
                  +91 9900089415
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-stone-500 text-sm">
              © {new Date().getFullYear()} Tech4Earth Mobility Foundation. All
              rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="#"
                className="text-stone-500 hover:text-stone-300 text-sm transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-stone-500 hover:text-stone-300 text-sm transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
